import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { fetchModulesApi, updateModuleApi, toggleModuleStatusApi, deleteModuleApi } from "../../services/api/boq/moduleService";

export default function ModulesList() {
  const [modules, setModules] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setLoading(true);
    try {
      const res = await fetchModulesApi();
      setModules(res.data || []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (mod) => {
    setEditingModuleId(mod.id);
    setEditingName(mod.name);
  };

  const handleUpdate = async (id) => {
    if (!editingName.trim()) {
      toast.error("Module name cannot be empty");
      return;
    }
    try {
      await updateModuleApi(id, { name: editingName });
      toast.success("Module updated");
      setEditingModuleId(null);
      loadModules();
    } catch {
      toast.error("Failed to update module");
    }
  };

  const handleToggleStatus = async (mod) => {
    try {
      await toggleModuleStatusApi(mod.id, { is_enabled: !mod.is_enabled });
      toast.success(`Module ${mod.is_enabled ? "disabled" : "enabled"}`);
      loadModules();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this module?")) return;
    try {
      await deleteModuleApi(id);
      toast.success("Module deleted");
      loadModules();
    } catch {
      toast.error("Failed to delete module");
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Module List</h2>

      {error && <p className="text-red-600 mb-2">{error}</p>}

      {loading ? (
        <p>Loading modules...</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {modules.length === 0 ? (
              <p>No modules found</p>
            ) : (
              modules.map((mod) => (
                <div
                  key={mod.id}
                  className="flex flex-col justify-between p-4 border rounded shadow hover:bg-gray-50"
                >
                  <div>
                    {editingModuleId === mod.id ? (
                      <input
                        type="text"
                        className="border rounded p-1 w-full mb-2"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                      />
                    ) : (
                      <p className="font-semibold">{mod.name}</p>
                    )}
                    <p className="text-sm text-gray-500">ID: {mod.id}</p>
                    <span
                      className={`px-2 py-1 rounded text-sm font-semibold inline-block mt-2 ${
                        mod.is_enabled
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {mod.is_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {editingModuleId === mod.id ? (
                      <button
                        onClick={() => handleUpdate(mod.id)}
                        className="bg-blue-600 text-white px-3 py-1 rounded"
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEdit(mod)}
                        className="bg-yellow-500 text-white px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                    )}

                    <button
                      onClick={() => handleToggleStatus(mod)}
                      className={`px-3 py-1 rounded text-white ${
                        mod.is_enabled ? "bg-red-500" : "bg-green-600"
                      }`}
                    >
                      {mod.is_enabled ? "Disable" : "Enable"}
                    </button>

                    <button
                      onClick={() => handleDelete(mod.id)}
                      className="bg-gray-600 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}